/**
 * WebsiteFooter — minimal compliance footer with phone + service area.
 */
import { Phone, Mail, MapPin } from 'lucide-react';

export default function WebsiteFooter({ config }) {
  const phone = config.phone_number || '';
  const phoneDisplay = config.phone_display || phone;
  const email = config.owner_email_public || '';
  const year = new Date().getFullYear();
  const tagline = config.footer?.tagline || '';
  const legal = config.footer?.legal || `© ${year} ${config.business_name} · Built on Agent9`;

  return (
    <footer className="bg-slate-900 text-slate-300" data-testid="ws-footer">
      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-14 grid lg:grid-cols-3 gap-10">
        <div>
          <div className="flex items-center gap-2.5 mb-3">
            {config.logo?.mark ? (
              <img
                src={config.logo.mark}
                alt={config.business_name}
                className="h-10 w-auto"
                data-testid="ws-footer-logo-img"
              />
            ) : (
              <span
                className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-black text-base"
                style={{ backgroundColor: config.brand?.primary || '#0f4c81' }}
              >
                {(config.business_name || 'A9').slice(0, 1)}
              </span>
            )}
            <span className="font-extrabold text-base text-white tracking-[0.18em] uppercase">
              {config.business_name}
            </span>
          </div>
          {tagline && <p className="text-sm leading-relaxed text-slate-400">{tagline}</p>}
        </div>

        <div>
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-4">Contact</h3>
          <ul className="space-y-2.5 text-sm">
            {phone && (
              <li>
                <a href={`tel:${phone.replace(/\s/g, '')}`} className="inline-flex items-center gap-2 hover:text-white">
                  <Phone size={14} /> {phoneDisplay}
                </a>
              </li>
            )}
            {email && (
              <li>
                <a href={`mailto:${email}`} className="inline-flex items-center gap-2 hover:text-white">
                  <Mail size={14} /> {email}
                </a>
              </li>
            )}
            {config.service_area && (
              <li className="inline-flex items-center gap-2">
                <MapPin size={14} /> {config.service_area}
              </li>
            )}
            {config.hours && <li className="text-slate-400">{config.hours}</li>}
          </ul>
        </div>

        <div>
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-4">
            Service areas
          </h3>
          <p className="text-sm leading-relaxed text-slate-400">
            {Array.isArray(config.service_areas) && config.service_areas.length
              ? config.service_areas.join(' · ')
              : config.service_area}
          </p>
        </div>
      </div>
      <div className="border-t border-slate-800">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 py-5 text-xs text-slate-500">{legal}</div>
      </div>
    </footer>
  );
}
